import { type RefObject, useLayoutEffect, useRef, useState } from "react";

const SPACIOUS_GAP_PX = 12;

type AdaptiveRatingGap = "gap-3" | "gap-1";

function computeAdaptiveGap(
	container: HTMLDivElement,
	primary: HTMLDivElement,
	laurel: HTMLDivElement,
): AdaptiveRatingGap {
	const availableWidth = container.clientWidth;
	const primaryWidth = primary.scrollWidth;
	const laurelWidth = laurel.offsetWidth;
	const fitsSpaciousGap =
		primaryWidth + laurelWidth + SPACIOUS_GAP_PX <= availableWidth;
	return fitsSpaciousGap ? "gap-3" : "gap-1";
}

/**
 * Picks gap-3 when rating text and laurel fit side-by-side; otherwise gap-1.
 * Re-evaluates on container or content size changes.
 */
export function useAdaptiveRatingGap(
	enabled: boolean,
	measureKey: string,
): {
	containerRef: RefObject<HTMLDivElement | null>;
	primaryRef: RefObject<HTMLDivElement | null>;
	laurelRef: RefObject<HTMLDivElement | null>;
	gapClass: AdaptiveRatingGap;
} {
	const containerRef = useRef<HTMLDivElement>(null);
	const primaryRef = useRef<HTMLDivElement>(null);
	const laurelRef = useRef<HTMLDivElement>(null);
	const [gapClass, setGapClass] = useState<AdaptiveRatingGap>("gap-1");

	useLayoutEffect(() => {
		if (!enabled || measureKey.length === 0) {
			return;
		}
		const container = containerRef.current;
		const primary = primaryRef.current;
		const laurel = laurelRef.current;
		if (!container || !primary || !laurel) {
			return;
		}
		const updateGap = (): void => {
			const currentContainer = containerRef.current;
			const currentPrimary = primaryRef.current;
			const currentLaurel = laurelRef.current;
			if (!currentContainer || !currentPrimary || !currentLaurel) {
				return;
			}
			setGapClass(
				computeAdaptiveGap(currentContainer, currentPrimary, currentLaurel),
			);
		};
		updateGap();
		const observer = new ResizeObserver(updateGap);
		observer.observe(container);
		observer.observe(primary);
		observer.observe(laurel);
		return () => observer.disconnect();
	}, [enabled, measureKey]);

	return { containerRef, primaryRef, laurelRef, gapClass };
}
